import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatBadgeModule } from '@angular/material/badge';
import { IconComponent } from '@program-suite/util-icons';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { InAppNotificationsStore, SiteConfigStore } from '@program-suite/data-common';
import { AuthStore } from '@program-suite/data-auth';
import { ModalService, ModalType } from '../../services/modal.service';
import { NotificationsModalComponent } from '../modals/notifications-modal/notifications-modal.component';

interface NavItems {
  name: string;
  route: string;
  type: string;
  icon: string;
  class?: string;
}

@Component({
  selector: 'lib-sidenav',
  imports: [CommonModule, MatBadgeModule, IconComponent, RouterLink, RouterLinkActive],
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavComponent implements OnInit {
  siteConfigStore = inject(SiteConfigStore);
  notificationsStore = inject(InAppNotificationsStore);
  userStore = inject(AuthStore);
  navItems: NavItems[] = [];
  authStore = inject(AuthStore);
  notificationIcon = 'notifications';
  logoutIcon = 'arrow-left-from-bracket';
  routes = input<Array<{ path: string }> | undefined>();
  iconSrc = input<string>('');
  logoSrc = input<string>('');
  router = inject(Router);

  imageSrc = this.iconSrc() || this.siteConfigStore.siteConfig()?.iconUrl;
  notificationsCount = 0;
  usersInitials = computed(() => {
    const user = this.authStore.user();
    return user ? `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}` : '';
  });
  currentUser = computed(() => {
    const user = this.authStore.user();
    return user ? `${user.firstName || ''} ${user.lastName || ''}` : '';
  });

  constructor(
    private modalService: ModalService,
  ) {}

  ngOnInit(): void {

    if (this.authStore.isLossRunProcessor()) {
      this.imageSrc = '/assets/branding/shasta-icon.png';
      this.navItems = [
        {
          name: 'Policies',
          route: 'policies',
          type: 'btn-side-nav',
          icon: 'shield-check',
        },
      ];
      return;
    }

    if(this.authStore.isAdmin()) {
      this.imageSrc = '/assets/branding/shasta-icon.png';

      this.navItems = [
        {
          name: 'Dashboard',
          route: 'dashboard',
          type: 'btn-side-nav',
          icon: 'table-columns',
        },
        {
          name: 'Pending Requests',
          route: 'pending-request',
          type: 'btn-side-nav',
          icon: 'alarm-clock',
        },
        {
          name: 'Start a Quote',
          route: this.authStore.isAdmin() ? 'quote' : 'quote/new',
          type: 'btn-small',
          icon: 'dollar',
        },
        {

          name: 'Submissions',
          route: 'submissions',
          type: 'btn-side-nav',
          icon: 'arrow-down-to-line',
        },
        {
          name: 'Policies',
          route: 'policies',
          type: 'btn-side-nav',
          icon: 'shield-check',
        },
        {
          name: 'Program Guidelines',
          route: 'program-guidelines',
          type: 'btn-side-nav',
          icon: 'list-check',
        },
        {
          name: 'Policy Forms',
          route: 'policy-forms',
          type: 'btn-side-nav',
          icon: 'table',
        },
        {
          name: 'Wholesale Settings',
          route: 'wholesalers',
          type: 'btn-side-nav',
          icon: 'boxes-stacked',
        },
        {
          name: 'Agencies',
          route: 'agencies',
          type: 'btn-side-nav',
          icon: 'briefcase',
        },
        {
          name: 'Agents',
          route: 'agents',
          type: 'btn-side-nav',
          icon: 'users',
        },
      ]
    } else {
      this.navItems = [
        {
          name: 'Start a Quote',
          route: 'quote/new',
          type: 'btn-small',
          icon: 'dollar',
          class: 'black-outline'
        },
        {
          name: 'Dashboard',
          route: 'dashboard',
          type: 'btn-side-nav',
          icon: 'table-columns',
        },
        {

          name: 'Submissions',
          route: 'submissions',
          type: 'btn-side-nav',
          icon: 'arrow-down-to-line',
        },
        {
          name: 'Policies',
          route: 'policies',
          type: 'btn-side-nav',
          icon: 'shield-check',
        },
        {
          name: 'Program Guidelines',
          route: 'program-guidelines',
          type: 'btn-side-nav',
          icon: 'list-check',
        },
        {
          name: 'Policy Forms',
          route: 'policy-forms',
          type: 'btn-side-nav',
          icon: 'table',
        }
      ]
    }
    if (this.authStore.user()?.roles?.includes('Wholesale Admin')) {
      this.navItems.push(
        {
          name: 'Wholesale Settings',
          route: 'wholesale-settings',
          type: 'btn-side-nav',
          icon: 'boxes-stacked',
        },
        {
          name: 'Agencies',
          route: 'agencies',
          type: 'btn-side-nav',
          icon: 'briefcase',
        },
      );
    }
    if (this.authStore.isWholesale()) {
      this.navItems.push(
        {
          name: 'Agents',
          route: 'agents',
          type: 'btn-side-nav',
          icon: 'users',
        },
      );
    }
  }
    
  ngAfterViewInit() {
    this.notificationsCount = (this.notificationsStore.inAppNotifications() as any[])?.length;
  }

  onMouseEnter() {
    if (this.authStore.isAdmin() || this.authStore.isLossRunProcessor()) {
      this.imageSrc = this.logoSrc() || '/assets/branding/shasta-logo.png';
    } else {
      this.imageSrc = this.logoSrc() || '';
    }
  }

  onMouseLeave() {
    if (this.authStore.isAdmin() || this.authStore.isLossRunProcessor()) {
      this.imageSrc = this.iconSrc() || '/assets/branding/shasta-icon.png';
    } else {
      this.imageSrc = this.iconSrc() || '';
    }
  }

  openNotifcations() {
    this.modalService.open(NotificationsModalComponent, {
      modalType: ModalType.Half,
    });
  }

  handleNavigation(route: string) {
    if (route === 'quote/new') {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([route]);
      });
    } else {
      this.router.navigate([route]);
    }
  }
}
